import { useRef, useCallback, useEffect } from 'react';


interface FrequencyPair {
    f1: number;
    f2: number;
}

interface DTMFFrequencies {
    [key: string]: FrequencyPair;
}

class DTMFTone {
    private context: AudioContext;
    private status: number = 0;
    private freq1: number;
    private freq2: number;
    private volume: number; // Add this property
    private osc1?: OscillatorNode;
    private osc2?: OscillatorNode;
    private gainNode?: GainNode;
    private filter?: BiquadFilterNode;

    constructor(context: AudioContext, freq1: number, freq2: number, volume: number = 0.1) {
        this.context = context;
        this.freq1 = freq1;
        this.freq2 = freq2;
        this.volume = volume;
    }

    private setup(): void {
        this.osc1 = this.context.createOscillator();
        this.osc2 = this.context.createOscillator();
        this.osc1.frequency.value = this.freq1;
        this.osc2.frequency.value = this.freq2;
        this.gainNode = this.context.createGain();
        this.gainNode.gain.value = this.volume;
        this.filter = this.context.createBiquadFilter();
        this.filter.type = "lowpass";
        this.filter.frequency.value = 8000;
        this.osc1.connect(this.gainNode);
        this.osc2.connect(this.gainNode);
        this.gainNode.connect(this.filter);
        this.filter.connect(this.context.destination);
    }

    public setVolume(volume: number): void {
        this.volume = volume;
        if (this.gainNode) {
            this.gainNode.gain.value = volume;
        }
    }

    public start(): void {
        if (this.status === 0) {
            this.setup();
            this.osc1!.start(0);
            this.osc2!.start(0);
            this.status = 1;
        }
    }

    public stop(): void {
        if (this.osc1 && this.osc2 && this.status === 1) {
            this.osc1.stop(0);
            this.osc2.stop(0);
            this.status = 0;

            this.osc1 = undefined;
            this.osc2 = undefined;
            this.gainNode = undefined;
            this.filter = undefined;
        }
    }

    public setFrequencies(freq1: number, freq2: number): void {
        this.freq1 = freq1;
        this.freq2 = freq2;
    }

    public getStatus(): number {
        return this.status;
    }
}

// DTMF frequency mappings
const dtmfFrequencies: DTMFFrequencies = {
    "1": { f1: 697, f2: 1209 },
    "2": { f1: 697, f2: 1336 },
    "3": { f1: 697, f2: 1477 },
    "4": { f1: 770, f2: 1209 },
    "5": { f1: 770, f2: 1336 },
    "6": { f1: 770, f2: 1477 },
    "7": { f1: 852, f2: 1209 },
    "8": { f1: 852, f2: 1336 },
    "9": { f1: 852, f2: 1477 },
    "*": { f1: 941, f2: 1209 },
    "0": { f1: 941, f2: 1336 },
    "#": { f1: 941, f2: 1477 },
    "+": { f1: 941, f2: 1209 }
};

interface UseDTMFOptions {
    enabled?: boolean;
    volume?: number;
    duration?: number;
}

export function useDTMF(options: UseDTMFOptions = {}) {
    const { enabled = true, volume = 0.25, duration = 200 } = options;

    const audioContextRef = useRef<AudioContext | null>(null);
    const dtmfToneRef = useRef<DTMFTone | null>(null);
    const currentToneRef = useRef<string | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (enabled) {
            try {
                audioContextRef.current = new AudioContext();
                dtmfToneRef.current = new DTMFTone(audioContextRef.current, 350, 440, volume);
            } catch (error) {
                console.warn("AudioContext not supported or failed to initialize:", error);
            }
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (dtmfToneRef.current && dtmfToneRef.current.getStatus() === 1) {
                dtmfToneRef.current.stop();
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
        };
    }, [enabled, volume]);

    const playTone = useCallback((key: string) => {
        if (!enabled || !dtmfToneRef.current || !audioContextRef.current) return;

        try {
            if (audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }

            const frequencies = dtmfFrequencies[key];
            if (frequencies) {
                dtmfToneRef.current.setFrequencies(frequencies.f1, frequencies.f2);
                dtmfToneRef.current.start();
                currentToneRef.current = key;
            }
        } catch (error) {
            console.warn("Failed to play DTMF tone:", error);
        }
    }, [enabled]);

    const stopTone = useCallback(() => {
        if (dtmfToneRef.current && currentToneRef.current) {
            dtmfToneRef.current.stop();
            currentToneRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const playToneForDuration = useCallback((key: string) => {
        playTone(key);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            stopTone();
        }, duration);
    }, [playTone, stopTone, duration]);

    const handleButtonPress = useCallback((key: string) => {
        playTone(key);
    }, [playTone]);

    const handleButtonRelease = useCallback(() => {
        stopTone();
    }, [stopTone]);

    return {
        playTone,
        stopTone,
        playToneForDuration,
        handleButtonPress,
        handleButtonRelease,
        isPlaying: currentToneRef.current !== null
    };
}